import classnames from 'classnames';
import type {ReactNode} from 'react';
import React from 'react';
import {ConfirmationModal} from '@albert-io/atomic';

import AnnotationEditor from './AnnotationEditor';
import AnnotationNoteIndicators from './AnnotationNoteIndicators';
import {useAnnotationModeStore} from './AnnotationModeStore';
import {useAnnotationGesture} from './useAnnotationGesture';
import {useAnnotations} from './useAnnotations';
import {useIsAnnotationModeAvailable} from './useIsAnnotationModeAvailable';

import './annotations-cursor.scss';
import './annotations-highlights.scss';

interface AnnotatableContentProps {
  regionId: string | null;
  /** Language of the rendered text ('es' when translated); null = original. */
  lang?: string | null;
  /**
   * Styles the region wrapper div (e.g. `contents` where it must stay
   * layout-transparent). Unavailable/flag-off renders bare children without it.
   */
  className?: string;
  /** False where the region's notes surface elsewhere, as a passage's do. */
  showNoteIndicators?: boolean;
  children: ReactNode;
}

/**
 * Wraps children so selections inside them become annotations (see
 * `useAnnotations`). Anchors index the region's normalized visible text
 * (annotationTextProjection.ts), not the source markdown.
 */
export default function AnnotatableContent({
  regionId,
  lang = null,
  className,
  showNoteIndicators = true,
  children
}: AnnotatableContentProps) {
  const isAnnotationModeAvailable = useIsAnnotationModeAvailable();

  if (!isAnnotationModeAvailable || !regionId) {
    return <>{children}</>;
  }

  return (
    <AnnotatableRegion
      regionId={regionId}
      lang={lang}
      className={className}
      showNoteIndicators={showNoteIndicators}
    >
      {children}
    </AnnotatableRegion>
  );
}

function AnnotatableRegion({
  regionId,
  lang,
  className,
  showNoteIndicators,
  children
}: {
  regionId: string;
  lang: string | null;
  className?: string;
  showNoteIndicators: boolean;
  children: ReactNode;
}) {
  const isAnnotationModeOn = useAnnotationModeStore((state) => state.isAnnotationModeOn);
  const {
    captureAnnotation,
    getAnnotationAtPoint,
    containerRef,
    container,
    getProjection,
    isMergeConfirmPending,
    confirmPendingMerge,
    cancelPendingMerge
  } = useAnnotations(regionId, lang);
  const {activeAnnotation, handleMouseUp, openEditor, closeEditor} = useAnnotationGesture({
    capture: captureAnnotation,
    // A plain click on an existing annotation opens its style editor.
    resolveAtPoint: (event) => getAnnotationAtPoint(event.clientX, event.clientY)
  });

  return (
    <>
      <div
        ref={containerRef}
        className={isAnnotationModeOn ? classnames(className, 'cursor-highlighter') : className}
        onMouseUp={handleMouseUp}
      >
        {children}
      </div>
      {/* Outside the container: a portal's events bubble through the React tree,
          so nesting it would feed indicator clicks back into `handleMouseUp`. */}
      {showNoteIndicators && (
        <AnnotationNoteIndicators
          regionId={regionId}
          container={container}
          getProjection={getProjection}
        />
      )}
      {isMergeConfirmPending && (
        // Placeholder presentation: design wants this anchored to the selection
        // like the style popover, with final copy TBD (#19010 Slack thread).
        <ConfirmationModal
          body='Do you want to merge these annotations?'
          confirmText='Merge'
          cancelText='Cancel'
          onConfirm={() => openEditor(confirmPendingMerge())}
          onCancel={() => {
            closeEditor();
            cancelPendingMerge();
          }}
        />
      )}
      {!isMergeConfirmPending && activeAnnotation && (
        <AnnotationEditor
          regionId={regionId}
          activeAnnotation={activeAnnotation}
          onClose={closeEditor}
        />
      )}
    </>
  );
}
